/**
 * 客訂收款（應收）純函式：標籤、金額運算、payload 組裝。
 * 金額一律以「分」整數運算再轉回，避免浮點誤差。
 */

export const COLLECTION_PLAN_STATUS_LABEL = {
  DRAFT: '草稿',
  APPROVED: '已核准',
  NEEDS_REAPPROVAL: '待重新核准',
  SUPERSEDED: '已被取代',
};

export const COLLECTION_PLAN_SOURCE_LABEL = {
  SYSTEM_SUBMIT: '系統預設（提交）',
  SYSTEM_APPROVE: '系統預設（核准）',
  LEGACY_BACKFILL: '歷史回填',
  MANUAL: '人工編列',
};

export const COLLECTION_RECEIPT_STATUS_LABEL = {
  DRAFT: '草稿',
  CONFIRMED: '已確認',
  VOIDED: '已沖銷',
};

export const COLLECTION_CASH_EVENT_LABEL = {
  PAYMENT_VOID: '收款沖銷',
  REFUND: '退款',
  REFUND_VOID: '退款沖銷',
};

export const LEGACY_RECONCILIATION_STATUS_LABEL = {
  UNKNOWN: '未核對',
  PARTIAL: '部分補登',
  VERIFIED: '已核對',
};

export function labelOf(map, value) {
  if (value == null || value === '') {
    return '—';
  }
  return map[value] ?? value;
}

const toCents = (value) => Math.round(Number(value || 0) * 100);
const fromCents = (cents) => cents / 100;

export function sumAmounts(values) {
  return fromCents(values.reduce((acc, v) => acc + toCents(v), 0));
}

export function subtractAmount(a, b) {
  return fromCents(toCents(a) - toCents(b));
}

/** 已核准計畫才可抵付；草稿／待重新核准沿用舊有效版本，由後端回傳決定。 */
export function isPlanApproved(plan) {
  return plan?.status === 'APPROVED';
}

/** 人工改版入口：客訂已提交（尚未核准）或已核准客訂待改版。 */
export function canEditManualPlan(customerOrderStatus, plan) {
  if (customerOrderStatus === 'SUBMITTED') {
    return true;
  }
  return Boolean(plan?.revisionRequired) || plan?.status === 'NEEDS_REAPPROVAL';
}

export function canApprovePlan(plan) {
  return plan?.status === 'DRAFT' && plan?.source === 'MANUAL';
}

/** 新增／確認一般收款：客訂已核准且未結案／中止。 */
export function canRecordCollection(customerOrderStatus) {
  return ['APPROVED', 'PARTIALLY_SHIPPED', 'SHIPPED'].includes(customerOrderStatus);
}

/** 沖銷／退款：CLOSED 須先重新開啟；TERMINATED 可處理既有款項。 */
export function canReverseCollection(customerOrderStatus) {
  return canRecordCollection(customerOrderStatus) || customerOrderStatus === 'TERMINATED';
}

/** 各期剩餘可抵付額 = 約定 − 已抵付（summary.phases）。 */
export function getPhaseRemaining(summaryPhases = []) {
  return summaryPhases.map((phase) => ({
    phaseId: phase.phaseId,
    phaseNo: phase.phaseNo,
    contractedAmount: phase.contractedAmount,
    creditedAmount: phase.creditedAmount,
    remaining: Math.max(0, subtractAmount(phase.contractedAmount, phase.creditedAmount)),
  }));
}

/** 確認實收的預設抵付：依期別順序填滿未抵付額，超出部分保留為未分配。 */
export function buildDefaultCredits(amount, summaryPhases = []) {
  let left = toCents(amount);
  const credits = [];
  getPhaseRemaining(summaryPhases)
    .sort((a, b) => a.phaseNo - b.phaseNo)
    .forEach((phase) => {
      const take = Math.min(left, toCents(phase.remaining));
      if (take > 0) {
        credits.push({ phaseId: phase.phaseId, amount: fromCents(take) });
        left -= take;
      }
    });
  return credits;
}

/** 回傳錯誤訊息或 null。 */
export function validateCredits(amount, credits, summaryPhases = []) {
  const remainingById = new Map(
    getPhaseRemaining(summaryPhases).map((phase) => [phase.phaseId, toCents(phase.remaining)]),
  );
  for (const credit of credits) {
    if (toCents(credit.amount) < 0) {
      return '抵付金額不可為負數';
    }
    if (toCents(credit.amount) > (remainingById.get(credit.phaseId) ?? 0)) {
      return '抵付金額超過該期未抵付額';
    }
  }
  if (toCents(sumAmounts(credits.map((c) => c.amount))) > toCents(amount)) {
    return '抵付合計不可超過實收金額';
  }
  return null;
}

/** 去掉 0 元抵付列。 */
export function compactCredits(credits) {
  return credits
    .filter((credit) => toCents(credit.amount) > 0)
    .map((credit) => ({ phaseId: credit.phaseId, amount: Number(credit.amount) }));
}

/**
 * 人工計畫 payload。`phases[]` 為表單列：{ value, paymentTermId, dueDate, stablePhaseId }。
 * PERCENTAGE 合計須 100；AMOUNT 合計須等於客訂總額。
 */
export function buildManualPlanPayload({
  inputMode,
  phases,
  expectedVersionNo,
  expectedCustomerOrderUpdatedAt,
}) {
  const valueKey = inputMode === 'AMOUNT' ? 'amount' : 'percentage';
  return {
    expectedVersionNo,
    expectedCustomerOrderUpdatedAt,
    inputMode,
    phases: phases.map((phase) => ({
      [valueKey]: Number(phase.value),
      paymentTermId: phase.paymentTermId || null,
      dueDate: phase.dueDate || null,
      ...(phase.stablePhaseId ? { stablePhaseId: phase.stablePhaseId } : {}),
    })),
  };
}

export function validateManualPlan({ inputMode, phases, totalAmount }) {
  if (phases.length === 0) {
    return '至少需要一期';
  }
  if (phases.some((phase) => !(Number(phase.value) > 0))) {
    return '每期金額或百分比須大於 0';
  }
  const total = sumAmounts(phases.map((phase) => phase.value));
  if (inputMode === 'PERCENTAGE' && toCents(total) !== 10000) {
    return `百分比合計須為 100（目前 ${total}）`;
  }
  if (inputMode === 'AMOUNT' && toCents(total) !== toCents(totalAmount)) {
    return `金額合計須等於客訂總額 ${totalAmount}（目前 ${total}）`;
  }
  return null;
}

/** 由現有計畫帶入表單列（改版時保留 stablePhaseId 以承接已抵付）。 */
export function planToFormPhases(plan) {
  const inputMode = plan?.inputMode === 'AMOUNT' ? 'AMOUNT' : 'PERCENTAGE';
  const phases = (plan?.phases ?? []).map((phase) => ({
    value: String(inputMode === 'AMOUNT' ? phase.amount : phase.percentage),
    paymentTermId: phase.paymentTermId ?? '',
    dueDate: phase.dueDate ?? '',
    stablePhaseId: phase.stablePhaseId ?? null,
  }));
  return { inputMode, phases };
}

/** `datetime-local` 值（YYYY-MM-DDTHH:mm）→ 帶本地時區位移的 ISO。 */
export function localDateTimeToApi(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const pad = (n) => String(n).padStart(2, '0');
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  return `${withSeconds}${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** API 時間 → `datetime-local` 值（本地時間）。 */
export function apiToLocalDateTime(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (n) => String(n).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function nowLocalDateTime() {
  return apiToLocalDateTime(new Date().toISOString());
}

/**
 * 收款列欄位 be doc 未列範例，容錯讀取：草稿版本號／狀態／金額／時間／期別抵付。
 */
export function mapReceiptRow(receipt) {
  return {
    ...receipt,
    id: receipt.id ?? receipt.receiptId,
    status: receipt.status ?? (receipt.confirmedAt ? 'CONFIRMED' : 'DRAFT'),
    versionNo: receipt.versionNo ?? receipt.draftVersionNo ?? receipt.version ?? null,
    amount: receipt.amount ?? receipt.draft?.amount ?? null,
    actualReceivedAt: receipt.actualReceivedAt ?? receipt.draft?.actualReceivedAt ?? null,
    remark: receipt.remark ?? receipt.draft?.remark ?? null,
    credits: receipt.credits ?? receipt.phaseCredits ?? [],
  };
}

/**
 * 收款沖銷 payload：全額，期別反向列＝該筆所有有效抵付，子單反向列＝該筆所有有效分配。
 * `allocations` 取自 GET allocations（以 receiptId 篩選，`effectiveAmount` 優先）。
 */
export function buildReceiptVoidPayload({ receipt, allocations = [], actualOccurredAt, reason }) {
  const phaseReversals = (receipt.credits ?? [])
    .map((credit) => {
      const effective = credit.effectiveAmount ?? credit.netAmount ?? credit.amount;
      return {
        phaseId: credit.phaseId,
        amount: Number(effective),
        expectedCreditedAmount: Number(effective),
      };
    })
    .filter((row) => toCents(row.amount) > 0);
  const salesReversals = allocations
    .filter((allocation) => allocation.receiptId === receipt.id)
    .map((allocation) => {
      const effective = allocation.effectiveAmount ?? allocation.amount;
      return {
        sourceAllocationId: allocation.id,
        salesOrderId: allocation.salesOrderId,
        amount: Number(effective),
        expectedAllocatedAmount: Number(effective),
      };
    })
    .filter((row) => toCents(row.amount) > 0);
  const available = receipt.availableAmount ?? receipt.netAmount ?? receipt.amount;
  return {
    sourceReceiptId: receipt.id,
    amount: Number(receipt.amount),
    actualOccurredAt,
    reason,
    expectedReceiptAvailable: Number(available),
    phaseReversals,
    salesReversals,
  };
}

/** 分配列目前有效額（扣除調整／退款後）；欄位名不確定時退回原額。 */
export function allocationEffectiveAmount(allocation) {
  return Number(allocation.effectiveAmount ?? allocation.currentAmount ?? allocation.amount ?? 0);
}

/** 期別抵付列目前有效額。 */
export function creditEffectiveAmount(credit) {
  return Number(credit.effectiveAmount ?? credit.netAmount ?? credit.amount ?? 0);
}

/** 實收目前可用淨額（扣除退款）。 */
export function receiptAvailableAmount(receipt) {
  return Number(receipt.availableAmount ?? receipt.netAmount ?? receipt.amount ?? 0);
}

/**
 * 退款 payload：使用者對該筆收款的各期抵付／各子單分配填要減少的金額（0 不送）。
 * 每列帶 expected 目前有效額，後端比對不符回 409。
 */
export function buildRefundPayload({
  receipt,
  amount,
  actualOccurredAt,
  reason,
  phaseInputs = [],
  salesInputs = [],
}) {
  return {
    sourceReceiptId: receipt.id,
    amount: Number(amount),
    actualOccurredAt,
    reason,
    expectedReceiptAvailable: receiptAvailableAmount(receipt),
    phaseReversals: phaseInputs
      .filter((row) => toCents(row.amount) > 0)
      .map((row) => ({
        phaseId: row.phaseId,
        amount: Number(row.amount),
        expectedCreditedAmount: Number(row.expected),
      })),
    salesReversals: salesInputs
      .filter((row) => toCents(row.amount) > 0)
      .map((row) => ({
        sourceAllocationId: row.allocationId,
        salesOrderId: row.salesOrderId,
        amount: Number(row.amount),
        expectedAllocatedAmount: Number(row.expected),
      })),
  };
}

export function validateRefund({ receipt, amount, phaseInputs = [], salesInputs = [] }) {
  if (!(Number(amount) > 0)) {
    return '退款金額須大於 0';
  }
  if (toCents(amount) > toCents(receiptAvailableAmount(receipt))) {
    return '退款金額超過該筆收款可用淨額';
  }
  const over = [...phaseInputs, ...salesInputs].some((row) => toCents(row.amount) > toCents(row.expected));
  if (over) {
    return '反向金額不可超過目前有效額';
  }
  if (toCents(sumAmounts(phaseInputs.map((row) => row.amount))) > toCents(amount)
    || toCents(sumAmounts(salesInputs.map((row) => row.amount))) > toCents(amount)) {
    return '期別或子單反向合計不可超過退款金額';
  }
  return null;
}
